import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Grid,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import BusinessIcon from '@mui/icons-material/Business';
import salesService from '../../../services/salesService';
import { useAuth, Branch } from '../../../contexts/AuthContext'; // ✅ Branch interface de AuthContext'ten
import { restaurantScreenApi } from '../../../services/restaurantApiService';
import { useTranslation } from 'react-i18next';

interface AddRestaurantSetupModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  // Düzenleme modu için props
  editMode?: boolean;
  restaurantScreenId?: number;
  quickSaleScreenId?: number;
  screenName?: string;
  screenBranchId?: string | null;
  onSaved?: () => void; // Kaydetme sonrası callback
}

const AddRestaurantSetupModal: React.FC<AddRestaurantSetupModalProps> = ({ 
  open, 
  onClose, 
  companyId,
  editMode = false,
  restaurantScreenId,
  quickSaleScreenId,
  screenName: initialScreenName,
  screenBranchId,
  onSaved
}) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth(); // ✅ AuthContext'ten şube listesi
  const [screenName, setScreenName] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Modal acilis/kapanis ve duzenleme modu hazirligi
  useEffect(() => {
    if (!open || !companyId) {
      setScreenName('');
      setSelectedBranch(null);
      setError(null);
      return;
    }

    setError(null);

    // Düzenleme modunda mevcut verileri yükle
    if (editMode) {
      setScreenName(initialScreenName || '');
      // Şube bilgisini yükle
      if (screenBranchId) {
        const branch = branches.find(b => b.id === screenBranchId);
        setSelectedBranch(branch || null);
      } else {
        setSelectedBranch(null);
      }
    } else {
      // Yeni ekran modu - varsayılan değerler
      setScreenName('');
      setSelectedBranch(null);
    }
  }, [open, companyId, editMode, initialScreenName, screenBranchId, branches]);

  const handleSave = async () => {
    if (!screenName || !companyId) {
      setError(t('addRestaurantSetupModal.errors.screenNameRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editMode && restaurantScreenId && quickSaleScreenId) {
        // ✅ Güncelleme modu - mevcut ekranları güncelle
        // 1. QuickSaleScreen'i güncelle
        await salesService.updateQuickSaleScreen({
          Id: quickSaleScreenId,
          Name: screenName,
          BranchId: selectedBranch?.id || null,
        });

        // 2. RestaurantScreen'i güncelle
        await restaurantScreenApi.update(restaurantScreenId, {
          Id: restaurantScreenId,
          Name: screenName,
          BranchId: selectedBranch?.id || null,
        });

        console.log("✅ Restoran Ekranı güncellendi");
        
        // Kaydetme sonrası callback'i çağır
        if (onSaved) {
          onSaved();
        }
        handleClose();
      } else {
        // ✅ Yeni ekran oluşturma modu
        // 1. Önce QuickSaleScreen oluştur (ScreenType='Restaurant')
        const quickSaleResponse = await salesService.createQuickSaleScreen({
          Name: screenName,
          BranchId: selectedBranch?.id || null,
          ScreenType: 'Restaurant', // ✅ Restaurant ekranı olduğunu belirtiyoruz
          AssignedPersonnelJson: null,
          PaymentOptionsJson: null,
          StockVisibilityJson: null,
          PrintingSettingsJson: null,
          PersonnelPermissionsJson: null,
          PrepaidSystemEnabled: false,
          ShowProductImages: true,
          UseFiscalDeviceForCash: false,
          AllowAccountCreationOnSale: false
        });

        console.log("QuickSaleScreen oluşturuldu:", quickSaleResponse);

        // 2. Sonra RestaurantScreen oluştur
        const restaurantResponse = await restaurantScreenApi.create({
          Name: screenName,
          BranchId: selectedBranch?.id || null,
          QuickSaleScreenId: quickSaleResponse.Id,
          DefaultTableCapacity: 4,
          EnableTableTransfer: true,
          EnableSplitBill: true
        });

        console.log("RestaurantScreen oluşturuldu:", restaurantResponse);

        // 3. ✅ QuickSaleScreen'i güncelle - RestaurantScreenId'yi bağla
        await salesService.updateQuickSaleScreen({
          Id: quickSaleResponse.Id,
          RestaurantScreenId: restaurantResponse.Id
        });

        console.log("✅ Yeni Restoran Ekranı başarıyla oluşturuldu (QuickSaleScreen güncellendi)");
        
        // Kaydetme sonrası callback'i çağır
        if (onSaved) {
          onSaved();
        }
        handleClose();
      }

    } catch (err: any) {
      console.error("Restoran ekranı kaydedilirken hata:", err);
      setError(err.response?.data?.Message || t('addRestaurantSetupModal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setScreenName('');
    setSelectedBranch(null);
    setError(null);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {editMode ? t('addRestaurantSetupModal.titleEdit') : t('addRestaurantSetupModal.titleCreate')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Ekran Adı */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('addRestaurantSetupModal.fields.screenNameLabel')}
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              required
              InputProps={{
                startAdornment: <RestaurantIcon color="action" sx={{ mr: 1 }} />
              }}
              helperText={t('addRestaurantSetupModal.fields.screenNameHelper')}
            />
          </Grid>

          {/* Atanan Şube */}
          <Grid item xs={12}>
            <Autocomplete
              options={branches.filter(b => b.id !== 'null')} // ✅ AuthContext'ten branches - "Şubeye Ait Değil" filtreli
              getOptionLabel={(option) => option.name}
              value={selectedBranch}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('addRestaurantSetupModal.fields.assignedBranchLabel')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('addRestaurantSetupModal.common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!screenName || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? t('addRestaurantSetupModal.common.saving') : (editMode ? t('addRestaurantSetupModal.common.update') : t('addRestaurantSetupModal.common.create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRestaurantSetupModal;
